import {
  Box,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
  styled,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useRef, useState } from 'react';
import Scrollbar from '../../components/Scrollbar';
import { fetchBankCardCodeReceiver } from '../../api/bankCard';
import { FONT_ENGLISH } from '../../theme/typography';
import Iconify from '../../components/Iconify';
import useTabs from '../../hooks/useTabs';
import useCollapse3dCode from '../../hooks/useCollapse3dCode';

export default function CardCodeReceiver() {
  const { openCardCodeReceiver, onCloseCardCodeReceiver } = useCollapse3dCode();

  const { enqueueSnackbar } = useSnackbar();
  const [list, setList] = useState(null);
  const timerRef = useRef();

  // ---------------------
  const { currentTab, onChangeTab } = useTabs(null);

  // ---------------------
  useEffect(() => {
    if (openCardCodeReceiver) {
      onChangeTab(null, openCardCodeReceiver);
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
      onChangeTab(null, null);
    }
  }, [openCardCodeReceiver]);

  // ---------------------
  const getCodes = async () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    try {
      const data = await fetchBankCardCodeReceiver(currentTab);
      setList(data);
    } catch (e) {
      enqueueSnackbar(e?.messages[0] || 'An error has occurred', { variant: 'error' });
    }

    timerRef.current = setTimeout(getCodes, 5000);
  };

  // ---------------------
  useEffect(() => {
    if (currentTab) {
      setList(null);
      getCodes();
    }
  }, [currentTab]);

  // ---------------------------------
  useEffect(() => {
    console.log('Cleanup');

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // ---------------------
  const toggleDrawer = () => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }

    onCloseCardCodeReceiver();
  };

  // ---------------------
  return (
    <Drawer
      anchor="right"
      open={!!openCardCodeReceiver}
      onClose={toggleDrawer()}
      sx={{ zIndex: 1600, '& .MuiPaper-root': { maxWidth: 350, width: '90%' } }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ pl: 2.5, pr: 0.5, pt: 1, mb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <StyledRipple />
          <Typography variant="subtitle2" color="primary.light" fontFamily={FONT_ENGLISH}>
            3D Payment Codes
          </Typography>
        </Stack>

        <IconButton onClick={() => onCloseCardCodeReceiver()}>
          <Iconify icon="eva:close-outline" />
        </IconButton>
      </Stack>

      <Divider />

      <Tabs value={currentTab} onChange={onChangeTab} sx={{ mx: 2, mb: 2 }}>
        <Tab
          disableRipple
          label="Karta"
          icon={<Iconify icon="ri:visa-line" sx={{ color: '#0056a3', width: 22, height: 22 }} />}
          value="karta"
        />
        <Tab disableRipple label="Papara" icon={<Iconify icon="logos:mastercard" />} value="papara" />
      </Tabs>

      {list === null ? (
        <Stack alignItems="center" sx={{ mt: 12, opacity: 0.5 }}>
          <CircularProgress size={40} color="inherit" />
        </Stack>
      ) : (
        <>
          {list.length > 0 ? (
            <Scrollbar
              sx={{
                height: 1,
                '& .simplebar-content': { height: 1, display: 'flex', flexDirection: 'column' },
              }}
            >
              {list.map((item) => (
                <Stack
                  key={item.id}
                  sx={{
                    mx: 2,
                    mb: 1,
                    border: '1px solid #ccc',
                    borderRadius: 0.7,
                    px: 1.5,
                    py: 1,
                    '&:hover': { borderColor: '#000' },
                  }}
                  spacing={1.5}
                >
                  {(item.six_digit_number || item.four_digit_number || item.type) && (
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Iconify icon="material-symbols:sms-outline" sx={{ width: 20, height: 20 }} />
                        <Typography variant="subtitle1" sx={{ fontFamily: FONT_ENGLISH, letterSpacing: 1 }}>
                          {item.six_digit_number || item.four_digit_number || '***'}
                        </Typography>
                      </Stack>

                      <Typography variant="caption" sx={{ fontFamily: FONT_ENGLISH, color: '#999' }}>
                        {item.type || '-'}
                      </Typography>
                    </Stack>
                  )}

                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: FONT_ENGLISH,
                      color: item.six_digit_number || item.four_digit_number || item.type ? '#333' : '#000',
                      direction: 'rtl',
                      textAlign: 'justify',
                    }}
                  >
                    {item.full_text || item.message || '-'}
                  </Typography>
                </Stack>
              ))}
            </Scrollbar>
          ) : (
            <Stack alignContent="center" justifyContent="center" spacing={1} sx={{ mt: 10, opacity: 0.3 }}>
              <Iconify icon="solar:password-outline" sx={{ width: 100, height: 100, m: 'auto' }} />
              <Typography variant="subtitle1" fontFamily={FONT_ENGLISH} sx={{ textAlign: 'center', direction: 'rtl' }}>
                3D payment codes have not been received.
              </Typography>
            </Stack>
          )}
        </>
      )}
    </Drawer>
  );
}

// ----------------------------------------------------

const StyledRipple = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.light,
  color: theme.palette.primary.lighter,
  width: 8,
  height: 8,
  borderRadius: '100%',
  position: 'relative',
  boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
  '&::after': {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    animation: 'ripple 1.2s infinite ease-in-out',
    border: '1px solid currentColor',
    content: '""',
  },
  '@keyframes ripple': {
    '0%': {
      transform: 'scale(.8)',
      opacity: 1,
    },
    '100%': {
      transform: 'scale(2)',
      opacity: 0,
    },
  },
}));
